import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { AlertCircle, Mic, Wifi, Settings, User, CheckCircle, Home, Church, Calendar, Video } from 'lucide-react';

const ProblemSolution = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });

  const problems = [
    { icon: Video, text: "Live streams look unprofessional" },
    { icon: Mic, text: "Bad audio distracts from the message" },
    { icon: Wifi, text: "Tech failures cause stress" },
    { icon: Settings, text: "Equipment feels overwhelming" },
    { icon: User, text: "You're stuck doing everything yourself" },
  ];

  const situations = [
    { icon: Home, text: "Teaching from your home" },
    { icon: Church, text: "Streaming from your church" },
    { icon: Calendar, text: "Hosting a live event" },
    { icon: Video, text: "Recording a course or podcast" },
  ];

  return (
    <section id="problem-solution" className="relative py-16 sm:py-20 md:py-24 lg:py-32 bg-obsidian" data-testid="problem-solution-section" ref={ref}>
      {/* Background decoration - Golden/amber line at top */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
        {/* THE PROBLEM Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16 md:mb-20"
        >
          {/* Main headline */}
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase mb-8 sm:mb-10 md:mb-12" data-testid="problem-heading">
            <span className="text-white">Too Many </span>
            <span 
              className="text-cyan-400"
              style={{ 
                textShadow: '0 0 20px rgba(0, 240, 255, 0.4), 0 0 40px rgba(0, 240, 255, 0.2)'
              }}
            >Powerful</span>
            <span className="text-white"> Messages Are</span>
            <br />
            <span className="text-white">Limited By </span>
            <span className="text-neon-pink">Technical Barriers</span>
          </h2>

          {/* Problem list */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 max-w-5xl mx-auto">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex flex-col items-center p-3 sm:p-4 bg-white/5 border border-white/10 hover:border-neon-pink/50 transition-colors"
              >
                <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-neon-pink mb-2 sm:mb-3" />
                <span className="text-xs sm:text-sm text-white/70 text-center">{problem.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* THE SOLUTION Section - No heading, no divider bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center"
        >
          <div className="max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12">
            <p className="text-sm sm:text-base md:text-lg text-white">
              We handle the cameras, switching, audio, lighting, streaming, and technical direction so you can focus fully on what God placed on your heart.
            </p>
          </div>

          {/* Whether you're... */}
          <p className="text-sm sm:text-base text-white mb-4 sm:mb-6">Whether you're:</p>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto mb-8 sm:mb-10 md:mb-12">
            {situations.map((situation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                className="flex flex-col items-center p-3 sm:p-4 bg-white/5 border border-white/10 hover:border-neon-blue/50 transition-colors"
              >
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-neon-blue mb-2 sm:mb-3" />
                <span className="text-xs sm:text-sm text-white/70 text-center">{situation.text}</span>
              </motion.div>
            ))}
          </div>

          {/* Final statement */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-xl sm:text-2xl md:text-3xl font-heading uppercase tracking-wide"
          >
            <span className="text-white">We bring the </span>
            <span 
              className="text-cyan-400"
              style={{ 
                textShadow: '0 0 20px rgba(0, 240, 255, 0.4), 0 0 40px rgba(0, 240, 255, 0.2)'
              }}
            >stage</span>
            <span className="text-white"> to you.</span>
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSolution;
